import { useState } from "react";
import { useAuth } from "@/lib/auth";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Label } from "@/components/ui/label";
import { ShieldCheck, Loader2, X } from "lucide-react";
import api from "@/lib/api";
import { toast } from "sonner";

/**
 * One-time consent capture modal shown to pre-iter32 users who have no
 * `age_group` on their profile. Renders as a full-screen overlay that cannot
 * be dismissed until consent is recorded — this is intentional (legal lock).
 */
export default function ConsentMigrationModal() {
  const { refresh } = useAuth();
  const [ageGroup, setAgeGroup] = useState("");
  const [parentName, setParentName] = useState("");
  const [consent, setConsent] = useState(false);
  const [busy, setBusy] = useState(false);

  const canSubmit = ageGroup && consent && (
    ageGroup === "under_13" ? parentName.trim().length > 1 : true
  );

  const submit = async () => {
    if (!canSubmit) return;
    setBusy(true);
    try {
      await api.post("/users/me/consent", {
        age_group: ageGroup,
        parent_guardian_name: ageGroup === "under_13" ? parentName.trim() : null,
        consent_accepted: consent,
      });
      await refresh();
      toast.success("Thanks — consent recorded");
    } catch (e) { toast.error(e?.response?.data?.detail || "Failed"); }
    finally { setBusy(false); }
  };

  return (
    <div data-testid="consent-migration-modal" className="fixed inset-0 z-[60] bg-black/85 backdrop-blur-sm flex items-center justify-center p-4">
      <div className="max-w-md w-full rounded-3xl border border-[#F5C462]/40 bg-gradient-to-br from-[#180D0D] to-[#0A0605] p-6 space-y-4">
        <div className="w-14 h-14 rounded-full bg-[#F5C462]/15 border border-[#F5C462]/40 flex items-center justify-center text-[#F5C462] mx-auto">
          <ShieldCheck size={26}/>
        </div>
        <div className="text-center">
          <h2 className="font-display text-xl font-bold text-white">One quick confirmation</h2>
          <p className="text-xs text-zinc-400 mt-2 leading-relaxed">
            NextScene is updating its safety policy. Please confirm your age group so we can keep the platform compliant for everyone. This will only be asked once.
          </p>
        </div>

        <div>
          <Label className="text-zinc-300 text-xs">Your age group</Label>
          <Select value={ageGroup} onValueChange={(v) => { setAgeGroup(v); setConsent(false); setParentName(""); }}>
            <SelectTrigger data-testid="migration-age" className="mt-1 bg-zinc-900 border-zinc-800"><SelectValue placeholder="Select age band"/></SelectTrigger>
            <SelectContent position="popper" side="bottom" align="start" className="z-[70] bg-[#180D0D] border-zinc-800 text-white">
              <SelectItem value="under_13" data-testid="migration-age-under-13">Under 13</SelectItem>
              <SelectItem value="13_17" data-testid="migration-age-13-17">13 to 17</SelectItem>
              <SelectItem value="18_plus" data-testid="migration-age-18-plus">18 and above</SelectItem>
            </SelectContent>
          </Select>
        </div>

        {ageGroup === "under_13" && (
          <div className="rounded-xl border border-[#F5C462]/30 bg-[#F5C462]/5 p-3 space-y-3">
            <p className="text-[11px] text-zinc-300">My parent/guardian is managing this account.</p>
            <div>
              <Label className="text-zinc-300 text-xs">Parent / Guardian Name</Label>
              <Input data-testid="migration-parent-name" value={parentName} onChange={(e) => setParentName(e.target.value)} placeholder="Full name" className="mt-1 bg-zinc-900 border-zinc-800"/>
            </div>
            <label className="flex items-start gap-2 cursor-pointer">
              <input data-testid="migration-consent-under13" type="checkbox" checked={consent} onChange={(e) => setConsent(e.target.checked)} className="mt-0.5 accent-[#DC2626] w-4 h-4"/>
              <span className="text-[11px] text-zinc-300 leading-relaxed">
                I confirm I am the parent/guardian and agree to the Terms of Service on behalf of my child.
              </span>
            </label>
          </div>
        )}
        {ageGroup === "13_17" && (
          <div className="rounded-xl border border-[#F5C462]/30 bg-[#F5C462]/5 p-3">
            <label className="flex items-start gap-2 cursor-pointer">
              <input data-testid="migration-consent-teen" type="checkbox" checked={consent} onChange={(e) => setConsent(e.target.checked)} className="mt-0.5 accent-[#DC2626] w-4 h-4"/>
              <span className="text-[11px] text-zinc-300 leading-relaxed">
                I have my parent/guardian's permission to use NextScene and accept the Terms of Service.
              </span>
            </label>
          </div>
        )}
        {ageGroup === "18_plus" && (
          <div className="rounded-xl border border-zinc-800 bg-zinc-900/40 p-3">
            <label className="flex items-start gap-2 cursor-pointer">
              <input data-testid="migration-consent-adult" type="checkbox" checked={consent} onChange={(e) => setConsent(e.target.checked)} className="mt-0.5 accent-[#DC2626] w-4 h-4"/>
              <span className="text-[11px] text-zinc-300 leading-relaxed">
                I am 18 or older and accept the Terms of Service and Privacy Policy.
              </span>
            </label>
          </div>
        )}

        <Button data-testid="migration-submit" disabled={!canSubmit || busy} onClick={submit} className="w-full h-11 rounded-full bg-[#DC2626] hover:bg-[#EF4444] text-white font-semibold disabled:opacity-40">
          {busy ? <Loader2 className="animate-spin mr-1" size={14}/> : null} Continue
        </Button>
        <p className="text-[10px] text-zinc-600 text-center leading-relaxed">Required to comply with digital safety guidelines for entertainment platforms in India.</p>
      </div>
    </div>
  );
}
